import { InputHTMLAttributes, ReactNode } from 'react'

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode
  description?: string
  error?: string
}

/**
 * Reusable checkbox component for consent forms
 * Mobile-first design with 44x44px touch target (FR-047)
 */
export function Checkbox({ label, description, error, className = '', ...props }: CheckboxProps) {
  const checkboxId = props.id || `checkbox-${Math.random().toString(36).substr(2, 9)}`

  const containerStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '16px',
    cursor: 'pointer',
    // Ensure entire area is tappable
    minHeight: '44px',
  }

  const checkboxWrapperStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // FR-047: 44x44px touch target
    minWidth: '44px',
    minHeight: '44px',
    marginRight: '12px',
    flexShrink: 0,
  }

  const checkboxStyles = {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    accentColor: '#007AFF',
  }

  const labelContainerStyles = {
    flex: 1,
    paddingTop: '10px', // Vertically center with checkbox
  }

  const labelStyles = {
    display: 'block',
    fontSize: '16px',
    fontWeight: 500,
    color: '#333',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    marginBottom: description ? '4px' : 0,
  }

  const descriptionStyles = {
    display: 'block',
    fontSize: '14px',
    color: '#666',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    marginTop: '4px',
  }

  const errorStyles = {
    display: 'block',
    marginTop: '6px',
    fontSize: '14px',
    color: '#dc3545',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  return (
    <div>
      <label htmlFor={checkboxId} style={containerStyles}>
        <div style={checkboxWrapperStyles}>
          <input
            id={checkboxId}
            type="checkbox"
            style={checkboxStyles}
            className={className}
            {...props}
          />
        </div>

        <div style={labelContainerStyles}>
          <span style={labelStyles}>{label}</span>
          {description && <span style={descriptionStyles}>{description}</span>}
        </div>
      </label>

      {error && <span style={errorStyles}>{error}</span>}
    </div>
  )
}
